import type {
    ExecutionDetail,
    ExecutionLink,
    ExecutionRun,
    ExecutionTaskEdge,
    ExecutionTaskNode,
} from '@zerohuman/shared'
import type { ExecutionNodeView } from '@zerohuman/ui'
import { layoutTaskGraph, type TaskGraphEdge } from '@/lib/taskGraph'

/*
 * BG §18.4 in graph form. `action` is the only tone that means a human has to move (blue); `paused` and
 * `queued` are both grey but keep separate names because they earn different icons on the node.
 */
export type ExecutionNodeTone =
    | 'unvisited'
    | 'running'
    | 'action'
    | 'paused'
    | 'queued'
    | 'succeeded'
    | 'failed'
    | 'cancelled'
export type ExecutionNodeState = {
    tone: ExecutionNodeTone
    label: string
    runCount: number
    completedCount: number
    activeCount: number
    runIds: string[]
}
export type ExecutionGraphNode = ExecutionTaskNode &
    ExecutionNodeState & {
        position: { x: number; y: number }
        selected: boolean
        isStart: boolean
    }
export type ExecutionGraphEdge = TaskGraphEdge & { versionIds: string[] }
export type ExecutionEdgeState = { taken: boolean; current: boolean; traversals: number }

const waitingStatuses = new Set([
    'waiting_on_gate',
    'waiting_on_run',
    'paused_spend',
    'paused_plan',
    'paused_team',
    'paused',
])
const queuedStatuses = new Set(['queued', 'pending'])

export function isCurrentExecutionRun(run: Pick<ExecutionRun, 'status' | 'finishedAt'>): boolean {
    return (
        run.finishedAt === null &&
        (run.status === 'running' || waitingStatuses.has(run.status) || queuedStatuses.has(run.status))
    )
}

const runStatusPresentation: Record<ExecutionRun['status'], { tone: ExecutionNodeTone; label: string }> = {
    running: { tone: 'running', label: 'Running' },
    waiting_on_gate: { tone: 'action', label: 'Awaiting approval' },
    waiting_on_run: { tone: 'paused', label: 'Waiting for a run' },
    paused_spend: { tone: 'paused', label: 'Spend paused' },
    paused_plan: { tone: 'paused', label: 'Plan paused' },
    paused_team: { tone: 'paused', label: 'Team off' },
    paused: { tone: 'paused', label: 'Paused' },
    pending: { tone: 'queued', label: 'Pending' },
    queued: { tone: 'queued', label: 'Queued' },
    succeeded: { tone: 'succeeded', label: 'Completed' },
    failed: { tone: 'failed', label: 'Failed' },
    blocked: { tone: 'failed', label: 'Blocked' },
    skipped_overlap: { tone: 'cancelled', label: 'Skipped' },
    cancelled: { tone: 'cancelled', label: 'Cancelled' },
}

/** A retry can replace an old failure, while concurrent runs always remain visible as current. */
export function executionNodeState(runs: ExecutionRun[]): ExecutionNodeState {
    // The API orders runs by causal links, including retries sharing a timestamp.
    const ordered = runs
    const active = ordered.filter(isCurrentExecutionRun)
    const running = active.find(run => run.status === 'running')
    const waiting = active.find(run => waitingStatuses.has(run.status))
    const latest = running ?? waiting ?? active.at(-1) ?? ordered.at(-1)
    const { tone, label } = latest
        ? runStatusPresentation[latest.status]
        : { tone: 'unvisited' as const, label: 'Not visited' }

    return {
        tone,
        label,
        runCount: ordered.length,
        completedCount: ordered.filter(run => run.status === 'succeeded').length,
        activeCount: active.length,
        runIds: ordered.map(run => run.id),
    }
}

/*
 * BG §18.4 as a lookup rather than a chain of ternaries in the template. A `Record` keyed by the
 * tone means adding a tone is a type error until it is given a colour, which is the whole reason the
 * minimap used to disagree with the nodes it was a map of: everything that was not succeeded or
 * running fell through to grey, including failures.
 */
const TONE_COLOUR: Record<ExecutionNodeTone, string> = {
    succeeded: 'var(--state-complete)',
    running: 'var(--state-active)',
    action: 'var(--state-action)',
    failed: 'var(--state-failed)',
    paused: 'var(--state-inactive)',
    queued: 'var(--state-inactive)',
    cancelled: 'var(--state-inactive)',
    unvisited: 'var(--state-inactive)',
}

/** The minimap draws from node data that Vue Flow types as optional, hence the undefined case. */
export function executionToneColour(tone: ExecutionNodeTone | undefined): string {
    return tone ? TONE_COLOUR[tone] : 'var(--state-inactive)'
}

/**
 * What the shared node draws (ZhExecutionNode): a task without a name shows its slug, and one without a
 * team says so, except the COO's, which works for the whole enterprise.
 */
export function executionNodeView(node: ExecutionTaskNode & ExecutionNodeState): ExecutionNodeView {
    return {
        name: node.name || node.slug,
        slug: node.slug,
        member: node.member,
        role: node.role,
        team: { name: node.team?.name || (node.role?.slug === 'coo' ? 'Enterprise' : 'No team') },
        tone: node.tone,
        label: node.label,
        runCount: node.runCount,
        activeCount: node.activeCount,
        missing: node.missing,
    }
}

/** Include topology only: polling must not refit or recompute Dagre for status changes. */
export function executionTopologyKey(graph: { nodes: ExecutionTaskNode[]; edges: ExecutionTaskEdge[] }): string {
    return JSON.stringify({
        nodes: graph.nodes.map(node => node.id).sort(),
        edges: graph.edges
            .map(edge => [edge.id, edge.source, edge.target, edge.on, edge.when])
            .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    })
}

export function buildExecutionLayout(graph: { nodes: ExecutionTaskNode[]; edges: ExecutionTaskEdge[] }) {
    const edges: ExecutionGraphEdge[] = graph.edges.map(edge => ({
        ...edge,
        dangling: false,
        parallelIndex: 0,
        parallelCount: 1,
        isBackEdge: false,
        returnLane: 0,
        points: [],
    }))
    const positions = layoutTaskGraph(
        graph.nodes.map(node => node.id),
        edges,
    )
    return { positions, edges }
}

/** Keep historical handoffs visible without guessing which parallel condition was taken. */
export function executionGraphTopology(execution: Pick<ExecutionDetail, 'graph' | 'runs' | 'links'>) {
    const runs = new Map(execution.runs.map(run => [run.id, run]))
    const nodes = new Map(execution.graph.nodes.map(node => [node.taskId ?? node.id, node.id]))
    const edges = new Map(execution.graph.edges.map(edge => [edge.id, edge]))
    for (const link of execution.links) {
        if (link.edgeIds.some(edgeId => edges.has(edgeId))) continue
        const sourceRun = runs.get(link.sourceRunId)
        const targetRun = runs.get(link.targetRunId)
        const source = sourceRun ? nodes.get(sourceRun.taskId) : undefined
        const target = targetRun ? nodes.get(targetRun.taskId) : undefined
        if (!source || !target) continue
        const id = recordedEdgeId(source, target, link.kind)
        edges.set(id, {
            id,
            source,
            target,
            on: link.kind === 'retry' ? 'Retry' : 'Recorded handoff',
            when: null,
            versionIds: [],
        })
    }
    return { nodes: execution.graph.nodes, edges: [...edges.values()] }
}

function recordedEdgeId(source: string, target: string, kind: ExecutionLink['kind']) {
    return `recorded:${JSON.stringify([source, target, kind])}`
}

/** A route is taken only when a causal link proves it; two completed nodes are not enough. */
export function executionEdgeStates(
    execution: Pick<ExecutionDetail, 'graph' | 'runs' | 'links'>,
): Map<string, ExecutionEdgeState> {
    const states = new Map<string, ExecutionEdgeState>()
    const runs = new Map(execution.runs.map(run => [run.id, run]))
    const nodes = new Map(execution.graph.nodes.map(node => [node.taskId ?? node.id, node.id]))
    const configuredIds = new Set(execution.graph.edges.map(edge => edge.id))
    for (const link of execution.links) {
        const sourceRun = runs.get(link.sourceRunId)
        const targetRun = runs.get(link.targetRunId)
        if (!sourceRun || !targetRun) continue
        const source = nodes.get(sourceRun.taskId)
        const target = nodes.get(targetRun.taskId)
        if (!source || !target) continue
        const known = link.edgeIds.filter(id => configuredIds.has(id))
        const edgeIds = known.length > 0 ? known : [recordedEdgeId(source, target, link.kind)]
        for (const id of edgeIds) {
            const previous = states.get(id)
            states.set(id, {
                taken: true,
                current: Boolean(previous?.current || isCurrentExecutionRun(targetRun)),
                traversals: (previous?.traversals ?? 0) + 1,
            })
        }
    }
    return states
}
